// AI Model Router
// Automatically routes tasks to the optimal AI model based on context

import {
  AITaskType,
  AITier,
  TaskContext,
  ModelConfig,
  AIRoutingResult,
  MODEL_CONFIGS,
  MODEL_COSTS,
  ModelOverrides,
  AIProvider,
} from '../../types/ai';

// ============================================================================
// ROUTING LOGIC
// ============================================================================

/**
 * Route a task to the optimal AI model
 */
export function routeToModel(context: TaskContext): AIRoutingResult {
  const { type, quoteValue, hasImages, complexity, isStrategic } = context;

  // Rule 0: Email classification → OpenAI (avoid Gemini rate limits during backfill)
  if (type === 'EMAIL_CLASSIFICATION') {
    return {
      config: {
        provider: 'OPENAI',
        model: 'gpt-4o-mini',
        tier: 'STANDARD',
        maxTokens: 2048,
        temperature: 0.7,
      },
      reason: 'Email triage routed to OpenAI to avoid Gemini rate limits',
      estimatedCost: estimateCost('STANDARD', 800, 400),
    };
  }

  // Rule 1: Strategic/self-improvement tasks → Premium (Claude)
  if (isStrategic || type === 'SELF_IMPROVEMENT_REVIEW' || type === 'WIN_LOSS_ANALYSIS') {
    return {
      config: MODEL_CONFIGS.PREMIUM,
      reason: 'Strategic task requires premium model for best analysis',
      estimatedCost: estimateCost('PREMIUM', 1500, 500),
    };
  }

  // Rule 2: High-value quotes → Premium
  if (type === 'QUOTE_PROPOSAL' && quoteValue && quoteValue > 5000) {
    return {
      config: MODEL_CONFIGS.PREMIUM,
      reason: `High-value quote ($${quoteValue}) warrants premium analysis`,
      estimatedCost: estimateCost('PREMIUM', 2000, 1000),
    };
  }

  // Rule 3: PDF/image scope extraction → Multimodal (Gemini)
  if (type === 'SCOPE_EXTRACTION_DOC' || (type === 'DOCUMENT_ANALYSIS' && hasImages) || type === 'RECEIPT_OCR') {
    return {
      config: MODEL_CONFIGS.MULTIMODAL,
      reason: 'Document analysis requires multimodal model',
      estimatedCost: estimateCost('MULTIMODAL', 2000, 1000),
    };
  }

  // Rule 3a: Image/document tasks → Multimodal (Gemini)
  if (hasImages || type === 'DOCUMENT_ANALYSIS') {
    return {
      config: MODEL_CONFIGS.MULTIMODAL,
      reason: 'Visual content requires multimodal model',
      estimatedCost: estimateCost('MULTIMODAL', 1000, 500),
    };
  }

  // Rule 4: Semantic search → Embeddings
  if (type === 'SEMANTIC_SEARCH') {
    return {
      config: MODEL_CONFIGS.EMBEDDINGS,
      reason: 'Semantic search uses embedding model',
      estimatedCost: estimateCost('EMBEDDINGS', 500, 0),
    };
  }

  // Rule 5: High-value quote builder tasks → Premium (Claude)
  // Scope extraction, labor generation, material suggestion for quotes > $5k
  if (
    (type === 'SCOPE_EXTRACTION' || type === 'LABOR_GENERATION' || type === 'MATERIAL_SUGGESTION') &&
    quoteValue && quoteValue > 5000
  ) {
    return {
      config: MODEL_CONFIGS.PREMIUM,
      reason: `High-value quote ($${quoteValue}) benefits from premium model accuracy`,
      estimatedCost: estimateCost('PREMIUM', 1500, 800),
    };
  }

  // Rule 6: Complex scope writing → Premium (for quality)
  if (type === 'SCOPE_WRITING' && complexity === 'HIGH') {
    return {
      config: MODEL_CONFIGS.PREMIUM,
      reason: 'Complex scope writing requires premium model',
      estimatedCost: estimateCost('PREMIUM', 1000, 600),
    };
  }

  // Rule 7: Complex tasks → Premium
  if (complexity === 'HIGH') {
    return {
      config: MODEL_CONFIGS.PREMIUM,
      reason: 'High complexity task benefits from premium model',
      estimatedCost: estimateCost('PREMIUM', 1500, 800),
    };
  }

  // Rule 8: Pricing recommendations → Premium (important for business)
  if (type === 'PRICING_RECOMMENDATION') {
    return {
      config: MODEL_CONFIGS.PREMIUM,
      reason: 'Pricing decisions require careful analysis',
      estimatedCost: estimateCost('PREMIUM', 1000, 500),
    };
  }

  // Default: Standard tasks → GPT-4o-mini (80% of requests)
  return {
    config: MODEL_CONFIGS.STANDARD,
    reason: 'Standard task uses cost-effective model',
    estimatedCost: estimateCost('STANDARD', 800, 400),
  };
}

/**
 * Estimate cost in cents for a request
 */
function estimateCost(tier: AITier, inputTokens: number, outputTokens: number): number {
  const config = MODEL_CONFIGS[tier];
  const costs = MODEL_COSTS[config.model];
  
  if (!costs) return 0;
  
  const inputCost = (inputTokens / 1_000_000) * costs.input;
  const outputCost = (outputTokens / 1_000_000) * costs.output;
  
  return Math.round((inputCost + outputCost) * 100) / 100; // Round to 2 decimal places
}

/**
 * Get model overrides from localStorage
 */
function getModelOverrides(): ModelOverrides {
  const stored = localStorage.getItem('ai_model_overrides');
  if (!stored) return {};
  try {
    return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to parse model overrides:', e);
    return {};
  }
}

/**
 * Save model overrides to localStorage
 */
function saveModelOverrides(overrides: ModelOverrides): void {
  localStorage.setItem('ai_model_overrides', JSON.stringify(overrides));
}

/**
 * Get tier-level model override
 */
export function getTierModelOverride(tier: AITier): ModelConfig | null {
  const overrides = getModelOverrides();
  if (overrides.tiers?.[tier]) {
    const override = overrides.tiers[tier]!;
    const defaultConfig = MODEL_CONFIGS[tier];
    return {
      provider: override.provider,
      model: override.model,
      tier,
      maxTokens: defaultConfig.maxTokens,
      temperature: defaultConfig.temperature,
    };
  }
  return null;
}

/**
 * Set tier-level model override
 */
export function setTierModelOverride(tier: AITier, provider: AIProvider, model: string): void {
  const overrides = getModelOverrides();
  if (!overrides.tiers) overrides.tiers = {};
  overrides.tiers[tier] = { provider, model };
  saveModelOverrides(overrides);
}

/**
 * Clear tier-level model override
 */
export function clearTierModelOverride(tier: AITier): void {
  const overrides = getModelOverrides();
  if (overrides.tiers) {
    delete overrides.tiers[tier];
    saveModelOverrides(overrides);
  }
}

/**
 * Get provider-level model override
 */
export function getProviderModelOverride(provider: AIProvider): string | null {
  const overrides = getModelOverrides();
  return overrides.providers?.[provider] || null;
}

/**
 * Set provider-level model override
 */
export function setProviderModelOverride(provider: AIProvider, model: string): void {
  const overrides = getModelOverrides();
  if (!overrides.providers) overrides.providers = {};
  overrides.providers[provider] = model;
  saveModelOverrides(overrides);
}

/**
 * Clear provider-level model override
 */
export function clearProviderModelOverride(provider: AIProvider): void {
  const overrides = getModelOverrides();
  if (overrides.providers) {
    delete overrides.providers[provider];
    saveModelOverrides(overrides);
  }
}

/**
 * Get task-specific model override if configured
 */
export function getTaskOverride(taskType: AITaskType): ModelConfig | null {
  const overrides = getModelOverrides();
  if (overrides.tasks?.[taskType]) {
    const override = overrides.tasks[taskType]!;
    // Try to find matching tier config for defaults
    const matchingTier = Object.entries(MODEL_CONFIGS).find(
      ([_, config]) => config.provider === override.provider
    )?.[1];
    
    return {
      provider: override.provider,
      model: override.model,
      tier: matchingTier?.tier || 'STANDARD',
      maxTokens: matchingTier?.maxTokens || 2048,
      temperature: matchingTier?.temperature || 0.7,
    };
  }
  
  // Legacy support: check old format
  const stored = localStorage.getItem('ai_task_overrides');
  if (stored) {
    try {
      const legacyOverrides: Record<AITaskType, AITier> = JSON.parse(stored);
      if (legacyOverrides[taskType]) {
        return MODEL_CONFIGS[legacyOverrides[taskType]];
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  return null;
}

/**
 * Set a task-specific model override
 */
export function setTaskOverride(taskType: AITaskType, tierOrConfig: AITier | ModelConfig): void {
  const overrides = getModelOverrides();
  if (!overrides.tasks) overrides.tasks = {};
  
  if (typeof tierOrConfig === 'string') {
    // Legacy: tier only
    const config = MODEL_CONFIGS[tierOrConfig];
    overrides.tasks[taskType] = { provider: config.provider, model: config.model };
  } else {
    // New: full config
    overrides.tasks[taskType] = { provider: tierOrConfig.provider, model: tierOrConfig.model };
  }
  
  saveModelOverrides(overrides);
}

/**
 * Clear a task-specific override
 */
export function clearTaskOverride(taskType: AITaskType): void {
  const overrides = getModelOverrides();
  if (overrides.tasks) {
    delete overrides.tasks[taskType];
    saveModelOverrides(overrides);
  }
}

// ============================================================================
// ROUTING WITH OVERRIDES
// ============================================================================

/**
 * Route to model, respecting any user-configured overrides
 * Priority: Task > Tier > Provider > Default
 */
export function routeWithOverrides(context: TaskContext): AIRoutingResult {
  // Priority 1: Check for task-specific override (highest priority)
  const taskOverride = getTaskOverride(context.type);
  if (taskOverride) {
    return {
      config: taskOverride,
      reason: `Task override: ${taskOverride.provider} ${taskOverride.model}`,
      estimatedCost: estimateCost(taskOverride.tier, 1000, 500),
    };
  }
  
  // Priority 2: Get default routing
  const defaultRouting = routeToModel(context);
  const tier = defaultRouting.config.tier;
  
  // Priority 3: Check for tier-level override
  const tierOverride = getTierModelOverride(tier);
  if (tierOverride) {
    return {
      config: tierOverride,
      reason: `Tier override: ${tierOverride.provider} ${tierOverride.model}`,
      estimatedCost: estimateCost(tier, 1000, 500),
    };
  }
  
  // Priority 4: Check for provider-level override
  const providerOverride = getProviderModelOverride(defaultRouting.config.provider);
  if (providerOverride) {
    return {
      config: {
        ...defaultRouting.config,
        model: providerOverride,
      },
      reason: `Provider override: ${providerOverride}`,
      estimatedCost: estimateCost(tier, 1000, 500),
    };
  }
  
  // Priority 5: Use default routing
  return defaultRouting;
}

// ============================================================================
// USAGE TRACKING
// ============================================================================

const USAGE_KEY = 'ai_usage_logs';

export interface UsageEntry {
  timestamp: string;
  taskType: AITaskType;
  tier: AITier;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  latencyMs: number;
  success: boolean;
}

export function logUsage(entry: Omit<UsageEntry, 'timestamp'>): void {
  const logs = getUsageLogs();
  logs.push({
    ...entry,
    timestamp: new Date().toISOString(),
  });
  
  // Keep only last 1000 entries
  if (logs.length > 1000) {
    logs.splice(0, logs.length - 1000);
  }
  
  localStorage.setItem(USAGE_KEY, JSON.stringify(logs));
}

export function getUsageLogs(): UsageEntry[] {
  const stored = localStorage.getItem(USAGE_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function getUsageStats(days: number = 30): {
  totalRequests: number;
  totalCost: number;
  byTier: Record<AITier, { requests: number; cost: number }>;
  avgLatency: number;
} {
  const logs = getUsageLogs();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  
  const recentLogs = logs.filter(l => new Date(l.timestamp) >= cutoff);
  
  const stats = {
    totalRequests: recentLogs.length,
    totalCost: 0,
    byTier: {
      PREMIUM: { requests: 0, cost: 0 },
      STANDARD: { requests: 0, cost: 0 },
      MULTIMODAL: { requests: 0, cost: 0 },
      EMBEDDINGS: { requests: 0, cost: 0 },
    } as Record<AITier, { requests: number; cost: number }>,
    avgLatency: 0,
  };
  
  let totalLatency = 0;
  
  for (const log of recentLogs) {
    stats.totalCost += log.cost;
    stats.byTier[log.tier].requests++;
    stats.byTier[log.tier].cost += log.cost;
    totalLatency += log.latencyMs;
  }
  
  stats.avgLatency = recentLogs.length > 0 ? Math.round(totalLatency / recentLogs.length) : 0;
  stats.totalCost = Math.round(stats.totalCost * 100) / 100;
  
  return stats;
}

/**
 * Reset all model overrides to defaults
 * Clears all tier, provider, and task overrides
 */
export function resetAllModelOverrides(): void {
  // Clear all overrides from localStorage
  localStorage.removeItem('ai_model_overrides');
  // Also clear legacy task overrides
  localStorage.removeItem('ai_task_overrides');
}

// Export router service
export const aiRouter = {
  routeToModel,
  routeWithOverrides,
  getTaskOverride,
  setTaskOverride,
  clearTaskOverride,
  getTierModelOverride,
  setTierModelOverride,
  clearTierModelOverride,
  getProviderModelOverride,
  setProviderModelOverride,
  clearProviderModelOverride,
  resetAllModelOverrides,
  logUsage,
  getUsageLogs,
  getUsageStats,
};

export default aiRouter;
